/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// React
import { useMemo, useState, type ReactElement } from 'react';

// Commerce SDK
import type { ShopperProducts } from '@/scapi';

// Utils
import { getDisplayVariationValues } from '@/lib/product/product-utils';
import { useTranslation } from 'react-i18next';

// Components
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * BundledProductItems component that displays bundled product items.
 *
 * Renders the "Product Includes" section for kits and sectionals, listing
 * each component piece with its quantity. The section is collapsible;
 * it defaults to expanded per the CART-02 spec.
 *
 * @param props - Component props
 * @param props.bundledProducts - Array of bundled products with full product details and quantity
 * @returns JSX element with collapsible bundled product items list or null if no items
 */
export default function BundledProductItems({
    bundledProducts,
}: {
    bundledProducts: ShopperProducts.schemas['BundledProduct'][];
}): ReactElement | null {
    const [isExpanded, setIsExpanded] = useState(true);

    const allVariationDisplayValues = useMemo(
        () =>
            bundledProducts.map((bundledProduct) =>
                getDisplayVariationValues(
                    bundledProduct.product.variationAttributes,
                    bundledProduct.product.variationValues
                )
            ),
        [bundledProducts]
    );
    const { t } = useTranslation('cart');

    if (!bundledProducts.length) return null;

    return (
        <div className="mt-2 space-y-2" data-testid="bundledProductItems">
            <button
                type="button"
                className="flex w-full items-center justify-between text-sm font-medium text-foreground"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((prev) => !prev)}>
                <span>{t('bundle.productIncludes')}:</span>
                {isExpanded ? (
                    <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                    <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                )}
            </button>

            {isExpanded && (
                <div className="space-y-2 pl-2">
                    {bundledProducts.map((bundledProduct, index) => {
                        const product = bundledProduct.product;
                        const variationDisplayValues = allVariationDisplayValues[index];

                        return (
                            <div key={product.id || index} className="space-y-0.5">
                                <div className="text-sm text-foreground">{product.name}</div>

                                {Object.keys(variationDisplayValues).length > 0 && (
                                    <div className="text-sm text-muted-foreground">
                                        {Object.entries(variationDisplayValues).map(([attrName, attrValue]) => (
                                            <span key={attrName}>
                                                {attrName}: {attrValue}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="text-sm text-muted-foreground">
                                    {t('attributes.quantity')} {bundledProduct.quantity}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
